<template>
  <Form @submit="submit" as="div">
    <template v-if="timeout !== undefined && timeout < 4">
      <div class="flex bg-black-50 text-para-s mb-10 md:mb-8 p-4" role="alert">
        <div class="mt-1 mr-2">
          <Icon name="invalid" class="w-4 h-4" />
        </div>
        <span v-if="timeout < 1" class="font-semibold">{{ labels.tokenExpired }}</span>
        <span v-else class="font-semibold">{{ labels.tokenAboutToExpire }}</span>
      </div>
    </template>

    <template v-if="tokenMissing">
      <div class="flex bg-black-50 text-para-s mb-10 md:mb-8 p-4" role="alert">
        <div class="mt-1 mr-2">
          <Icon name="invalid" class="w-4 h-4" />
        </div>
        <span class="font-semibold">{{ labels.tokenMissing }}</span>
      </div>
    </template>

    <slot />
  </Form>
</template>

<script lang="ts">
import { defineComponent, PropType, toRaw } from 'vue';
import { Form } from 'vee-validate';
import axios from 'axios';
import Utils from '../../utils/Utils';
import DynamicsUtils from '../../utils/DynamicsUtils';
import Enums from '../../utils/Enums';
import Icon from '../base/Icon.vue';

export interface FormLabels {
    tokenAboutToExpire: string;
    tokenExpired: string;
    tokenMissing: string;
}

export interface DynamicsFieldMapping {
    FormFieldName: string;
    DataverseFieldName: string;
}

export default defineComponent({
    name: 'VueForm',
    components: { Icon, Form },
    props: {
        uuid: { type: String, required: true },
        agreed: { type: Boolean, default: false },
        labels: { type: Object as PropType<FormLabels>, required: true },
        dynamicsSend: { type: Boolean, default: false },
        dynamicsFormId: { type: String },
        dynamicsApiUrl: { type: String },
        dynamicsLibUrl: { type: String },
        dynamicsFieldMappings: { type: Array as PropType<Array<DynamicsFieldMapping>>, default: () => [] },
    },
    data() {
        return {
            formElement: null as HTMLFormElement | null,
            genericDynamicsFieldMappings: null,
            timeout: undefined as number | undefined,
            timerInterval: null as any,
            tokenMissing: false,
        };
    },
    watch: {
        async agreed(newValue) {
            if (newValue) {
                this.submit();
            }
        },
    },
    created(): void {
        if (this.dynamicsSend && this.dynamicsLibUrl) {
            Utils.addScript(this.dynamicsLibUrl);
        }
    },
    async mounted(): Promise<void> {
        this.formElement = this.$el.querySelector('form');

        // Scroll logic for Clean URLs
        this.$nextTick(() => {
            const resultBlock = document.getElementById(`result-${this.uuid}`);
            if (resultBlock) {
                resultBlock.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
            }
        });

        // 1. Fetch CSRF token if missing
        const csrfInput = this.$el.querySelector('#csrf') as HTMLInputElement;
        if (csrfInput && !csrfInput.value) {
            try {
                const contextPath = Utils.getLocalStorage(Enums.STORAGE_KEY.CONTEXT_PATH) || '';
                const res = await axios.get(`${contextPath}${Enums.API.LOCATION}${Enums.API.CSRF}`);
                if (res.status === 200) {
                    csrfInput.value = res.data;
                } else if (res.status === 204) {
                    this.tokenMissing = true;
                }
            } catch (e) {
                console.error("Form: CSRF fetch failed", e);
            }
        }

        // 2. CSRF Timeout Monitoring
        await this.updateRemainingTime();
        this.timerInterval = setInterval(() => {
            if (this.timeout !== undefined && this.timeout > 0) {
                this.timeout--;
            }
        }, 60000); // Decrement every minute

        this.addEventListeners();
        DynamicsUtils.loadGenericFieldMappings().then(mappings => (this.genericDynamicsFieldMappings = mappings));
    },
    beforeUnmount() {
        if (this.timerInterval) {
            clearInterval(this.timerInterval);
        }
    },
    methods: {
        submit(): void {
            if (!this.formElement) return;

            // tracking form submit
            this.pushEvent('lz_form_submit');

            if (this.dynamicsSend) {
                DynamicsUtils.submitDynamicsForm(
                    this.formElement,
                    toRaw(this.genericDynamicsFieldMappings),
                    this.dynamicsFieldMappings,
                    this.dynamicsFormId,
                    this.dynamicsApiUrl,
                    this.formElement.submit.bind(this.formElement)
                );
            } else {
                this.formElement.submit();
            }
        },
        addEventListeners() {
            if (this.formElement) {
                this.formElement.addEventListener('focusin', () => this.pushEvent('lz_form_start'), { once: true });
            }
        },
        pushEvent(event: string) {
            if ((window as any).dataLayer) {
                (window as any).dataLayer.push({ event });
            }
        },
        async updateRemainingTime() {
            try {
                const contextPath = Utils.getLocalStorage(Enums.STORAGE_KEY.CONTEXT_PATH) || '';
                const res = await axios.get(`${contextPath}${Enums.API.LOCATION}${Enums.API.CSRF_TIMEOUT}`);
                if (res.status === 200) {
                    this.timeout = parseInt(res.data);
                }
            } catch (e) {
                console.warn("Form: Could not fetch CSRF timeout");
            }
        },
    },
});
</script>
